import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';

import { PREVENTIVE_TASK_PATH } from '../../../../config/paths';

import {
  clearPreventiveTask,
  getPreventiveTask,
  putPreventiveTask,
} from '../../../../actions/task-schedule/PreventiveTask';

import {
  breadcrumbsPropTypes,
  matchPropTypes,
} from '../../../common/resource/proptypes/CommonPropTypes';

import Content from '../../../layout/Content';
import Loader from '../../../common/Loader';
import NoDataResource from '../../../common/resource/NoDataResource';
import PreventiveTaskForm from './PreventiveTaskForm';

import {
  ACTIVITY_ACTION,
  MAINTENANCE_SERVICE_ORDER_PRIORITY,
  PREVENTIVE_TASK_MONTHLY_TYPE,
  PREVENTIVE_TASK_REPEAT_TYPE,
  PREVENTIVE_TASK_WEEK_ORDER,
  PREVENTIVE_TASK_WEEKDAY,
} from '../../../../config/constants';

const getSelectValue = (field) => {
  if (field && typeof field === 'object') return field.value;
  return field;
};

const toNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  return Number(value);
};

const isLastDayChecked = (value) =>
  value === true || value === 1 || value === '1';

const getEnumByValue = (enumObject, value) => {
  if (value === null || value === undefined) return null;

  return (
    Object.keys(enumObject)
      .map((key) => enumObject[key])
      .find((item) => item.value === value) || null
  );
};

const buildRepeatInterval = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  if (
    repeatType === PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_DAYS.value ||
    repeatType === PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_RULE.value
  ) {
    return 1;
  }

  return Number(formValues.repeatInterval || 1);
};

const buildWeekdays = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  if (repeatType !== PREVENTIVE_TASK_REPEAT_TYPE.WEEKLY.value) {
    return [];
  }

  if (!Array.isArray(formValues.weekdays)) {
    return [];
  }

  return formValues.weekdays
    .filter((item) => item && item.weekday)
    .map((item) => ({
      weekday: Number(item.weekday),
    }));
};

const buildMonthlyDays = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  if (repeatType !== PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_DAYS.value) {
    return [];
  }

  if (!Array.isArray(formValues.monthlyDays)) {
    return [];
  }

  return formValues.monthlyDays
    .filter((item) => item)
    .map((item) => {
      const lastDay = isLastDayChecked(item.isLastDayOfMonth);

      return {
        dayOfMonth: lastDay ? null : toNumberOrNull(item.dayOfMonth),
        isLastDayOfMonth: lastDay,
      };
    });
};

const buildMonthlyRules = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  if (repeatType !== PREVENTIVE_TASK_REPEAT_TYPE.MONTHLY_RULE.value) {
    return [];
  }

  if (!Array.isArray(formValues.monthlyRules)) {
    return [];
  }

  return formValues.monthlyRules
    .filter((item) => item && item.monthlyType)
    .map((item) => {
      const monthlyType = getSelectValue(item.monthlyType);

      return {
        monthlyType,
        weekOrder:
          monthlyType ===
          PREVENTIVE_TASK_MONTHLY_TYPE.LAST_WEEKDAY_OF_MONTH.value
            ? PREVENTIVE_TASK_WEEK_ORDER.LAST.value
            : getSelectValue(item.weekOrder),
        weekday: getSelectValue(item.weekday),
      };
    });
};

const buildChecklistItems = (formValues) => {
  if (!Array.isArray(formValues.checklistItems)) {
    return [];
  }

  return formValues.checklistItems
    .filter((item) => item && item.name)
    .map((item) => ({
      name: item.name,
      description: item.description || null,
    }));
};

const buildPreventiveTaskBody = (formValues) => {
  const repeatType = getSelectValue(formValues.repeatType);

  return {
    preventiveTask: {
      id: formValues.id,
      companyPreventiveTaskId: formValues.companyPreventiveTaskId,
      name: formValues.name,
      description: formValues.description || null,
      actionType: getSelectValue(formValues.actionType),
      active: getSelectValue(formValues.active),
      priority: getSelectValue(formValues.priority),
      deviation: toNumberOrNull(formValues.deviation),
      repeatType,
      repeatInterval: buildRepeatInterval(formValues),
    },
    weekdays: buildWeekdays(formValues),
    monthlyDays: buildMonthlyDays(formValues),
    monthlyRules: buildMonthlyRules(formValues),
    checklistItems: buildChecklistItems(formValues),
  };
};

const generateInitialValues = ({
  id,
  companyPreventiveTaskId,
  name,
  description,
  actionType,
  active,
  priority,
  deviation,
  repeatType,
  repeatInterval,
  weekdays,
  monthlyDays,
  monthlyRules,
  checklistItems,
}) => ({
  id,
  companyPreventiveTaskId,
  name,
  description,
  actionType: actionType ? ACTIVITY_ACTION[actionType] : null,
  active: {
    value: active,
    label: active ? 'Activo' : 'Inactivo',
  },
  priority: priority ? MAINTENANCE_SERVICE_ORDER_PRIORITY[priority] : null,
  deviation,
  repeatType: repeatType ? PREVENTIVE_TASK_REPEAT_TYPE[repeatType] : null,
  repeatInterval,

  weekdays: Array.isArray(weekdays)
    ? weekdays.map((item) => ({
        weekday: item.weekday,
      }))
    : [],

  monthlyDays:
    Array.isArray(monthlyDays) && monthlyDays.length
      ? monthlyDays.map((item) => ({
          dayOfMonth: item.dayOfMonth,
          isLastDayOfMonth:
            item.isLastDayOfMonth || item.lastDayOfMonth || false,
        }))
      : [
          {
            dayOfMonth: null,
            isLastDayOfMonth: false,
          },
        ],

  monthlyRules:
    Array.isArray(monthlyRules) && monthlyRules.length
      ? monthlyRules.map((item) => ({
          monthlyType:
            typeof item.monthlyType === 'number'
              ? getEnumByValue(PREVENTIVE_TASK_MONTHLY_TYPE, item.monthlyType)
              : PREVENTIVE_TASK_MONTHLY_TYPE[item.monthlyType],
          weekOrder:
            item.weekOrder !== null && item.weekOrder !== undefined
              ? getEnumByValue(PREVENTIVE_TASK_WEEK_ORDER, item.weekOrder)
              : null,
          weekday: getEnumByValue(PREVENTIVE_TASK_WEEKDAY, item.weekday),
        }))
      : [
          {
            monthlyType: PREVENTIVE_TASK_MONTHLY_TYPE.NTH_WEEKDAY_OF_MONTH,
            weekOrder: PREVENTIVE_TASK_WEEK_ORDER.THIRD,
            weekday: PREVENTIVE_TASK_WEEKDAY.TUESDAY,
          },
        ],

  checklistItems: Array.isArray(checklistItems)
    ? checklistItems.map((item) => ({
        preventiveTaskId: item.preventiveTaskId,
        name: item.name,
        description: item.description,
      }))
    : [],
});

const EditPreventiveTask = ({
  breadcrumbs,
  dispatchGetPreventiveTask,
  dispatchClearPreventiveTask,
  dispatchPutPreventiveTask,
  match: {
    params: { id: preventiveTaskId },
  },
  loading,
  preventiveTask,
}) => {
  useLayoutEffect(() => {
    dispatchGetPreventiveTask({ preventiveTaskId });

    return () => dispatchClearPreventiveTask();
  }, []);

  const onSubmit = (formValues) => {
    const body = buildPreventiveTaskBody(formValues);

    dispatchPutPreventiveTask(preventiveTaskId, body);
  };

  let content;

  if (loading) content = <Loader />;
  else if (preventiveTask.isEmpty())
    content = <NoDataResource returnPage={PREVENTIVE_TASK_PATH} />;
  else
    content = (
      <PreventiveTaskForm
        onSubmit={onSubmit}
        loading={loading}
        initialValues={generateInitialValues(preventiveTask.toJS())}
        edit
      />
    );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      title="Editar Tarea Programada"
      subtitle="Editar tarea programada"
      content={content}
    />
  );
};

EditPreventiveTask.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  dispatchClearPreventiveTask: PropTypes.func.isRequired,
  dispatchGetPreventiveTask: PropTypes.func.isRequired,
  dispatchPutPreventiveTask: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  match: matchPropTypes.isRequired,
  preventiveTask: PropTypes.instanceOf(Immutable.Map).isRequired,
};

EditPreventiveTask.defaultProps = {
  loading: false,
};

const mapDispatchToProps = {
  dispatchClearPreventiveTask: clearPreventiveTask,
  dispatchGetPreventiveTask: getPreventiveTask,
  dispatchPutPreventiveTask: putPreventiveTask,
};

const mapStateToProps = (
  { TaskScheduleUnit },
  {
    match: {
      params: { id },
    },
  },
) => ({
  breadcrumbs: [
    ...TaskScheduleUnit.UnitHome.get('breadcrumbs'),
    {
      text: 'Tareas Programadas',
      href: PREVENTIVE_TASK_PATH,
    },
    {
      text: 'Ver',
      href: `${PREVENTIVE_TASK_PATH}/${id}`,
    },
    {
      text: 'Editar',
      href: '',
    },
  ],
  preventiveTask: TaskScheduleUnit.PreventiveTask.getIn(['current', 'content']),
  loading: !TaskScheduleUnit.PreventiveTask.getIn([
    'current',
    'activity',
  ]).isEmpty(),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditPreventiveTask);
